import { useCallback, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Resolver, SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { ECustomerType } from '@aqx-core/common/api/account';
import { ISignUpCrmBasicParams } from '@aqx-core/common/api/auth';
import { EPasswordCriteriaType } from '@aqx-core/common/constants/password';
import { useBaseForm } from '@aqx-core/common/hooks/form/use-base-form';
import { signUpCrmViewController } from '@aqx-core/common/services/sign-up-crm/controller';
import { EUISnackBarMessageType, snackbarController } from '@aqx-core/common/services/snackbar';
import {
  isValidEmail,
  validatePasswordForLirunexCrm,
  validatePasswordForMitraderCrm
} from '@aqx-core/common/validators';

export interface ISignUpBasicValues extends ISignUpCrmBasicParams {
  tncAcknowledged: boolean;
}

export const useSignUpCrmForm = (passwordCriteriaType: EPasswordCriteriaType = EPasswordCriteriaType.LIRUNEX_CRM) => {
  const { t } = useTranslation();

  const passwordValidatorFn = useMemo(() => {
    if (passwordCriteriaType === EPasswordCriteriaType.MITRADER_CRM) {
      return validatePasswordForMitraderCrm;
    }
    return validatePasswordForLirunexCrm;
  }, [passwordCriteriaType]);

  const schema = useMemo(
    () =>
      yup.object().shape({
        email: yup
          .string()
          .required(() => t('Please enter a valid email.'))
          .test(
            'is-valid-email',
            () => t('Please enter a valid email.'),
            (value) => (value ? isValidEmail(value) : false)
          ),
        password: yup
          .string()
          .required(() => t('Please enter a valid password.'))
          .test(
            'is-strong-password',
            () => t('Please enter a valid password.'),
            (value) => !!value && passwordValidatorFn(value)
          ),
        customerType: yup
          .mixed<ECustomerType>()
          .oneOf(Object.values(ECustomerType) as ECustomerType[])
          .required(() => t('Customer type is required.')),
        countryId: yup.number().required(() => t('Please select a country.')),
        tncAcknowledged: yup
          .boolean()
          .oneOf([true], () => t('Please review and accept the Terms and Conditions.'))
          .required(() => t('Please review and accept the Terms and Conditions.'))
      }),
    [passwordValidatorFn, t]
  );

  const resolver = useMemo(() => yupResolver(schema) as Resolver<ISignUpBasicValues>, [schema]);

  const useFormMethods = useBaseForm<ISignUpBasicValues>({
    resolver,
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: {
      customerType: ECustomerType.INDIVIDUAL
    }
  });

  const { handleSubmit, trigger, watch, formState } = useFormMethods;

  const [email, password, customerType, countryId, tncAcknowledged] = watch([
    'email',
    'password',
    'customerType',
    'countryId',
    'tncAcknowledged'
  ]);

  const {
    errors: {
      email: emailError,
      password: passwordError,
      customerType: customerTypeError,
      countryId: countryIdError,
      tncAcknowledged: tncAcknowledgedError
    }
  } = formState;

  const isDisabled = useMemo(() => {
    return !!(
      !email ||
      !password ||
      !customerType ||
      !countryId ||
      !tncAcknowledged ||
      emailError ||
      passwordError ||
      customerTypeError ||
      countryIdError ||
      tncAcknowledgedError
    );
  }, [
    countryId,
    countryIdError,
    customerType,
    customerTypeError,
    email,
    emailError,
    password,
    passwordError,
    tncAcknowledged,
    tncAcknowledgedError
  ]);

  const onError = useCallback((message: string) => {
    snackbarController.showAlert({
      message,
      messageType: EUISnackBarMessageType.ERROR
    });
  }, []);

  const handleFormSubmit: SubmitHandler<ISignUpBasicValues> = useCallback(
    (values) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { tncAcknowledged, ...rest } = values;

      signUpCrmViewController.completeBasicInfoStep({ basicInfo: { ...rest }, onError, onSuccess: () => {} });
    },
    [onError]
  );

  const submit = useCallback(
    (e?: React.FormEvent<HTMLFormElement>) => {
      handleSubmit(handleFormSubmit)(e);
    },
    [handleSubmit, handleFormSubmit]
  );

  return {
    useFormMethods,
    submit,
    trigger,
    isDisabled,
    email,
    password,
    customerType,
    countryId,
    tncAcknowledged
  };
};
